import React, { useState, useEffect } from "react";
import {
  Table,
  Spin,
  Input,
  Button,
  Tag,
  Modal,
  Select,
  message,
  Tooltip,
} from "antd";
import moment from "moment";
import Spinner from "../../components/spinner";
import styles, * as Styled from "./style.js";
import {
  defaultOptions,
  checkStatus,
  parseJSON,
  getLocationOrigin,
} from "../../config/api-connect";
import { getData } from "../../config/api-connect";
import {
  DoubleLeftOutlined,
  CloseOutlined,
  DeleteOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { FaAngleDoubleLeft } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { DatePicker } from "antd";
import { getCurrentFinancialYear } from "../../utils/helper";
import ReactHTMLTableToExcel from "react-html-table-to-excel";

const { RangePicker } = DatePicker;

const { Option } = Select;

const LeaveApplications = () => {
  const [leaveAppData, setLeaveAppData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const startDate = params.get("start_date");
  const endDate = params.get("end_date");
  const [expandedRows, setExpandedRows] = useState({});
  const [fyStart, fyEnd] = getCurrentFinancialYear();
  const [dateRange, setDateRange] = useState([
    startDate ? moment(startDate, "YYYY-MM-DD") : fyStart,
    endDate ? moment(endDate, "YYYY-MM-DD") : fyEnd,
  ]);
  const [viewType, setViewType] = useState("team");

  const fetchLeaveApplications = () => {
    setLoading(true);

    const [startMoment, endMoment] = dateRange;
    const start_date = startMoment ? startMoment.format("YYYY-MM-DD") : "";
    const end_date = endMoment ? endMoment.format("YYYY-MM-DD") : "";

    getData(
      `/buddy/get_leave_applications_for_admin/?status=All&start_date=${start_date}&end_date=${end_date}&view_type=${viewType}`,
    )
      .then((response) => {
        if (response?.data) {
          const formatted = response.data.map((item, index) => ({
            key: index,
            application_id: item.id,
            employee: item.buddy_name,
            mobile_no: item.mobile_no,
            role: item.role,
            no_of_days: item.no_of_days,
            reason: item.reason,
            leave_type: item.leave_type,
            start_date: moment(item.start_date).format("MMM DD, YYYY"),
            end_date: moment(item.end_date).format("MMM DD, YYYY"),
            status: item.status,
            raw_start: item.start_date,
            raw_end: item.end_date,
            handled_buddy: item.handled_buddy,
          }));

          setLeaveAppData(formatted);
        }
      })
      .catch((err) => {
        console.error("Error fetching leave applications:", err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLeaveApplications();
  }, [dateRange, viewType]);

  const toggleExpand = (key) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleApproveReject = async (type) => {
    if (
      type === "approve" &&
      (selectedLeaveType == null || selectedLeaveType === "")
    ) {
      message.warning("Please select a leave type before approving.");
      window.alert("Please select a leave type");
      return;
    }
    const confirmMsg =
      type === "reject"
        ? "Do you want to reject this leave application?"
        : "Do you want to approve leave for this application?";

    Modal.confirm({
      title: confirmMsg,
      onOk: async () => {
        if (
          type === "approve" &&
          (selectedLeaveType == null || selectedLeaveType === "")
        ) {
          message.warning("Please select a leave type before approving.");
          window.alert("Please select a leave type");
          return;
        }
        const status = type === "approve" ? 10 : 20;
        const url = `${getLocationOrigin()}/buddy/submit_leave_application/`;
        const options = { ...defaultOptions };
        options.method = "post";
        options.headers = { "content-type": "application/json" };
        options.body = JSON.stringify({
          application_id: selectedApplication?.application_id,
          status,
          leave_type: type === "approve" ? selectedLeaveType : null,
        });

        try {
          const res = await fetch(url, options)
            .then(checkStatus)
            .then(parseJSON);
          message.success(res.Message);
          setSelectedLeaveType(null);
          setIsModalOpen(false);
          fetchLeaveApplications();
        } catch (err) {
          message.error(err.toString());
        }
      },
    });
  };

  const handleTagClick = (record) => {
    setSelectedApplication(record);
    setLoading(true);
    setSelectedLeaveType(null);

    getData(
      `/buddy/leave_application_details/?application_id=${record.application_id}`,
    )
      .then((res) => {
        if (res?.leave_types) {
          setLeaveTypes(res.leave_types);
        }

        setIsModalOpen(true);
      })
      .catch((err) => {
        console.error("Error fetching leave types:", err);
      })
      .finally(() => setLoading(false));
  };

  const baseColumns = [
    { title: "Employee", dataIndex: "employee", key: "employee" },
    { title: "Mobile No", dataIndex: "mobile_no", key: "mobile_no" },
    { title: "Role", dataIndex: "role", key: "role" },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (text, record) => {
        if (!text) return null;

        const maxChars = 60;
        const totalChars = text.length;
        const isLong = totalChars > maxChars;

        const displayText = expandedRows[record.key]
          ? text
          : text.substring(0, maxChars) + (isLong ? "..." : "");

        return (
          <div
            style={{
              maxWidth: 300,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <span>{displayText}</span>
            {isLong && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, marginLeft: 4 }}
                onClick={() => toggleExpand(record.key)}
              >
                {expandedRows[record.key] ? "Read less" : "Read more"}
              </Button>
            )}
          </div>
        );
      },
    },

    { title: "Start Date", dataIndex: "start_date", key: "start_date" },
    { title: "End Date", dataIndex: "end_date", key: "end_date" },
    { title: "Leave Type", dataIndex: "leave_type", key: "leave_type" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const lowerStatus = status?.toLowerCase();

        if (lowerStatus === "leave applied") {
          return (
            <div>
              <Tag
                color="gold"
                style={{ cursor: "pointer" }}
                onClick={() => handleTagClick(record)}
              >
                Pending
              </Tag>
              <div style={{ fontSize: "12px", color: "#888" }}>
                (Tap to change)
              </div>
            </div>
          );
        }

        if (lowerStatus === "leave approved") {
          return (
            <Tag
              color="green"
              style={{ cursor: "not-allowed", pointerEvents: "none" }}
            >
              Approved
            </Tag>
          );
        }

        if (lowerStatus === "leave rejected") {
          return (
            <Tag
              color="red"
              style={{ cursor: "not-allowed", pointerEvents: "none" }}
            >
              Rejected
            </Tag>
          );
        }

        return null;
      },
    },
  ];

  let columns = [...baseColumns];

  if (filterStatus === "Approved") {
    columns.splice(6, 0, {
      title: "Leave Type",
      dataIndex: "leave_type",
      key: "leave_type",
    });
  }

  if (filterStatus === "Approved" || filterStatus === "Rejected") {
    columns.push({
      title: "Handled By",
      dataIndex: "handled_buddy",
      key: "handled_buddy",
    });
  }

  const filteredData = leaveAppData
    .filter((item) => {
      if (filterStatus === "All") return true;
      if (filterStatus === "Pending")
        return item.status?.toLowerCase() === "leave applied";
      if (filterStatus === "Approved")
        return item.status?.toLowerCase() === "leave approved";
      if (filterStatus === "Rejected")
        return item.status?.toLowerCase() === "leave rejected";
      return true;
    })
    .filter((item) => {
      const fieldsToSearch = [
        item.employee,
        item.mobile_no,
        item.role,
        item.reason,
        item.start_date,
        item.end_date,
      ];
      return fieldsToSearch
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase());
    });

  return (
    <Spin spinning={loading} indicator={<Spinner />}>
      <Styled.MainContainer>
        <Styled.Header>
          <Tooltip title="Back">
            <a
              style={{ cursor: "pointer", color: "#02475c" }}
              onClick={() => navigate(-1)}
            >
              <FaAngleDoubleLeft />{" "}
            </a>
          </Tooltip>
          <div>Leave Applications</div>
          <ReactHTMLTableToExcel
            className="btn btn-sm downloadBtn"
            table="leave-applications-download"
            filename={
              "Leave Applications " +
              dateRange[0].format("DD-MM-YYYY") +
              " to " +
              dateRange[1].format("DD-MM-YYYY")
            }
            sheet="Leave Applications"
            buttonText="Download"
          />
        </Styled.Header>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "12px 16px",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: "8px" }}>
            {["All", "Pending", "Approved", "Rejected"].map((status) => (
              <Button
                key={status}
                type={filterStatus === status ? "primary" : "default"}
                onClick={() => setFilterStatus(status)}
                style={
                  filterStatus === status
                    ? {
                        backgroundColor: "rgb(2, 71, 92)",
                        borderColor: "rgb(2, 71, 92)",
                      }
                    : {}
                }
              >
                {status}
              </Button>
            ))}
            {localStorage.getItem("user_role") === "Trespasser" && (
              <Select
                value={viewType}
                onChange={setViewType}
                style={{ width: 180 }}
              >
                <Option value="team">My Team / Centers</Option>
                <Option value="all">All Applications</Option>
              </Select>
            )}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [])}
              format="DD-MM-YYYY"
              allowClear={false}
            />
            <Input
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
          </div>
        </div>
        <div className="recordCount">{filteredData.length} records</div>
        <Table
          className="ant-table-thead table_container"
          // tableLayout="fixed"
          dataSource={filteredData}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: false }}
        />

        <table id="leave-applications-download" style={{ display: "none" }}>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Mobile No</th>
              <th>Role</th>
              <th>Reason</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>No of Days</th>
              <th>Leave Type</th>
              <th>Status</th>
              <th>Handled By</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.map((item) => (
              <tr key={item.application_id}>
                <td>{item.employee}</td>
                <td>{item.mobile_no}</td>
                <td>{item.role}</td>
                <td>{item.reason}</td>
                <td>{item.start_date}</td>
                <td>{item.end_date}</td>
                <td>{item.no_of_days}</td>
                <td>{item.leave_type}</td>
                <td>{item.status}</td>
                <td>{item.handled_buddy}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <Modal
          title={
            <span style={{ fontWeight: "bold" }}>Approve or Reject Leave</span>
          }
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
        >
          {selectedApplication && (
            <div style={{ marginBottom: 16 }}>
              {[
                { label: "Employee Name", value: selectedApplication.employee },
                { label: "Mobile No", value: selectedApplication.mobile_no },
                { label: "Role", value: selectedApplication.role },
                { label: "No of Days", value: selectedApplication.no_of_days },
                { label: "Start Date", value: selectedApplication.start_date },
                { label: "End Date", value: selectedApplication.end_date },
                { label: "Reason", value: selectedApplication.reason },
              ].map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    marginBottom: 6,
                  }}
                >
                  <strong
                    style={{
                      minWidth: 130,
                      display: "flex",
                      alignItems: "flex-start",
                      fontWeight: "800",
                    }}
                  >
                    {item.label}
                  </strong>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <div>Choose leave type</div>
            <Select
              placeholder="--Select leave type--"
              style={{ width: "100%" }}
              value={selectedLeaveType}
              onChange={setSelectedLeaveType}
            >
              {leaveTypes.map((lt) => (
                <Option key={lt.id} value={lt.id}>
                  {lt.type}
                </Option>
              ))}
            </Select>
          </div>
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
          >
            <Button
              type="primary"
              icon={<DeleteOutlined />}
              style={{
                backgroundColor: "rgb(2, 71, 92)",
                borderColor: "rgb(2, 71, 92)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fff";
                e.currentTarget.style.color = "rgb(2, 71, 92)";
                e.currentTarget.style.borderColor = "rgb(2, 71, 92)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgb(2, 71, 92)";
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.borderColor = "rgb(2, 71, 92)";
              }}
              onClick={() => handleApproveReject("reject")}
            >
              Reject
            </Button>

            <Button
              type="primary"
              icon={<CheckOutlined />}
              style={{
                backgroundColor: "rgb(2, 71, 92)",
                borderColor: "rgb(2, 71, 92)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fff";
                e.currentTarget.style.color = "rgb(2, 71, 92)";
                e.currentTarget.style.borderColor = "rgb(2, 71, 92)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgb(2, 71, 92)";
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.borderColor = "rgb(2, 71, 92)";
              }}
              onClick={() => handleApproveReject("approve")}
            >
              Approve
            </Button>
          </div>
        </Modal>
      </Styled.MainContainer>
    </Spin>
  );
};

export default LeaveApplications;
